// components/BillingInformation.js
import React, { useState, useEffect } from "react";
import { MessageBox } from "reusableComponents";
import styles from "./styles.module.scss";

const BillingInformation = ({ 
  billingType, 
  setBillingType, 
  billingInfo, 
  setBillingInfo,
  t,
  errors = {},
  isUpdating = false,
  onUpdate,
  userStatus = "individual",
  scrollToBilling = false // New prop to trigger scrolling
}) => {
  const [touchedFields, setTouchedFields] = useState({});
  const [localBillingInfo, setLocalBillingInfo] = useState(billingInfo);
  const [isSubjectToVat, setIsSubjectToVat] = useState(true);
  const [isManualSwitch, setIsManualSwitch] = useState(false);
  const billingSectionRef = React.useRef(null);

  // Set billing type based on user status when component mounts
  useEffect(() => {
    // Only set default if not manually switched by user
    if (!isManualSwitch) {
      if (userStatus === "company") {
        setBillingType("company");
      } else {
        setBillingType("individual");
      }
    }
  }, [userStatus, setBillingType, isManualSwitch]);

  useEffect(() => {
    setLocalBillingInfo(billingInfo);
    // Initialize VAT checkbox based on existing VAT number
    const hasVatNumber = billingInfo.companyVatNumber && 
                         billingInfo.companyVatNumber.trim() !== '';
    
    if (billingInfo.vat_taxable_person !== undefined) {
      setIsSubjectToVat(billingInfo.vat_taxable_person === 1);
    } else {
      setIsSubjectToVat(hasVatNumber);
    }
  }, [billingInfo]);

  // ============================================================
  // SCROLL TO BILLING SECTION WHEN ERRORS OCCUR
  // ============================================================
  useEffect(() => {
    if (scrollToBilling && hasAnyBillingErrors()) {
      setTimeout(() => {
        if (billingSectionRef.current) {
          const headerOffset = 80;
          const elementPosition = billingSectionRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          // Also try scrollIntoView as fallback
          billingSectionRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300);
    }
  }, [scrollToBilling]);

  const isLoading = isUpdating;

  const handleFieldChange = (field, value) => {
    const newValue = value;
    setLocalBillingInfo(prev => ({ ...prev, [field]: newValue }));
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    setBillingInfo(prev => ({ ...prev, [field]: newValue }));
  };

  const handleVatCheckboxChange = (checked) => {
    setIsSubjectToVat(checked);
    // CRITICAL: Update vat_taxable_person in billingInfo
    setBillingInfo(prev => ({ 
      ...prev, 
      vat_taxable_person: checked ? 1 : 0 
    }));
    if (!checked) {
      // Clear VAT number when not subject to VAT
      handleFieldChange('companyVatNumber', '');
    }
  };

  const hasFieldError = (fieldName) => {
    if (fieldName === 'companyVatNumber' && !isSubjectToVat) {
      return false;
    }
    return !!errors[fieldName];
  };

  const handleBillingTypeChange = (type) => {
    setIsManualSwitch(true);
    setBillingType(type);
    setTouchedFields({});
    // Also update user_status in billingInfo
    setBillingInfo(prev => ({ 
      ...prev, 
      user_status: type 
    }));
  };

  const hasAnyBillingErrors = () => {
    // Filter out VAT error if not subject to VAT
    const filteredErrors = { ...errors };
    if (!isSubjectToVat && filteredErrors.companyVatNumber) {
      delete filteredErrors.companyVatNumber;
    }
    return Object.keys(filteredErrors).length > 0;
  };

  return (
    <div 
      ref={billingSectionRef} 
      className={`${styles.billingSection} ${hasAnyBillingErrors() ? styles.hasErrors : ''}`}
      id="billing-section"
    >
      <div className={styles.sectionHeader}>
        <h2 className={styles.title}>
          {t("booking.fullBookingPage.billing.title")}
        </h2>
      </div>
      
      <div className={styles.billingForm}>
        {/* Billing Type Toggle */}
        <div className={styles.billingTypeToggle}>
          <button 
            className={`${styles.typeButton} ${billingType === "individual" ? styles.active : ""}`} 
            onClick={() => handleBillingTypeChange("individual")}
            disabled={isLoading}
          >
            {t("booking.fullBookingPage.billing.individual")}
          </button>
          <button 
            className={`${styles.typeButton} ${billingType === "company" ? styles.active : ""}`} 
            onClick={() => handleBillingTypeChange("company")}
            disabled={isLoading}
          >
            {t("booking.fullBookingPage.billing.company")}
          </button>
        </div>
        
        {billingType === "individual" ? (
          <>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <label>{t("booking.fullBookingPage.billing.firstName")} *</label>
                <input 
                  type="text" 
                  value={localBillingInfo.individualFirstName || billingInfo.individualFirstName} 
                  onChange={(e) => handleFieldChange('individualFirstName', e.target.value)} 
                  onBlur={() => setTouchedFields(prev => ({ ...prev, individualFirstName: true }))}
                  className={`${styles.input} ${hasFieldError('individualFirstName') ? styles.inputError : ''}`}
                  placeholder={t("booking.fullBookingPage.billing.firstNamePlaceholder")}
                  disabled={isLoading}
                />
              </div>
              <div className={styles.formField}>
                <label>{t("booking.fullBookingPage.billing.lastName")} *</label>
                <input 
                  type="text" 
                  value={localBillingInfo.individualLastName || billingInfo.individualLastName} 
                  onChange={(e) => handleFieldChange('individualLastName', e.target.value)} 
                  onBlur={() => setTouchedFields(prev => ({ ...prev, individualLastName: true }))}
                  className={`${styles.input} ${hasFieldError('individualLastName') ? styles.inputError : ''}`}
                  placeholder={t("booking.fullBookingPage.billing.lastNamePlaceholder")}
                  disabled={isLoading}
                />
              </div>
            </div>
            
            <div className={styles.formField}>
              <label>{t("booking.fullBookingPage.billing.address")} *</label>
              <input 
                type="text" 
                value={localBillingInfo.individualAddress || billingInfo.individualAddress} 
                onChange={(e) => handleFieldChange('individualAddress', e.target.value)} 
                onBlur={() => setTouchedFields(prev => ({ ...prev, individualAddress: true }))}
                className={`${styles.input} ${hasFieldError('individualAddress') ? styles.inputError : ''}`}
                placeholder={t("booking.fullBookingPage.billing.addressPlaceholder")}
                disabled={isLoading}
              />
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <label>{t("booking.fullBookingPage.billing.postalCode")} *</label>
                <input 
                  type="text" 
                  value={localBillingInfo.individualPostalCode || billingInfo.individualPostalCode} 
                  onChange={(e) => handleFieldChange('individualPostalCode', e.target.value)} 
                  onBlur={() => setTouchedFields(prev => ({ ...prev, individualPostalCode: true }))}
                  className={`${styles.input} ${hasFieldError('individualPostalCode') ? styles.inputError : ''}`}
                  placeholder={t("booking.fullBookingPage.billing.postalCodePlaceholder")}
                  disabled={isLoading}
                />
              </div>
              <div className={styles.formField}>
                <label>{t("booking.fullBookingPage.billing.city")} *</label>
                <input 
                  type="text" 
                  value={localBillingInfo.individualCity || billingInfo.individualCity} 
                  onChange={(e) => handleFieldChange('individualCity', e.target.value)} 
                  onBlur={() => setTouchedFields(prev => ({ ...prev, individualCity: true }))}
                  className={`${styles.input} ${hasFieldError('individualCity') ? styles.inputError : ''}`}
                  placeholder={t("booking.fullBookingPage.billing.cityPlaceholder")}
                  disabled={isLoading}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.formField}>
              <label>{t("booking.fullBookingPage.billing.companyName")} *</label>
              <input 
                type="text" 
                value={localBillingInfo.companyName || billingInfo.companyName} 
                onChange={(e) => handleFieldChange('companyName', e.target.value)} 
                onBlur={() => setTouchedFields(prev => ({ ...prev, companyName: true }))}
                className={`${styles.input} ${hasFieldError('companyName') ? styles.inputError : ''}`}
                placeholder={t("booking.fullBookingPage.billing.companyNamePlaceholder")}
                disabled={isLoading}
              />
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <label>{t("booking.fullBookingPage.billing.siret")} *</label>
                <input 
                  type="text" 
                  value={localBillingInfo.companySiret || billingInfo.companySiret} 
                  onChange={(e) => handleFieldChange('companySiret', e.target.value)} 
                  onBlur={() => setTouchedFields(prev => ({ ...prev, companySiret: true }))}
                  className={`${styles.input} ${hasFieldError('companySiret') ? styles.inputError : ''}`}
                  placeholder={t("booking.fullBookingPage.billing.siretPlaceholder")}
                  disabled={isLoading}
                />
              </div>
            </div>
            
            {/* VAT Checkbox and Field */}
            <div className={styles.vatSection}>
              <label className={styles.checkboxLabel}>
                <input 
                  type="checkbox"
                  checked={isSubjectToVat}
                  onChange={(e) => handleVatCheckboxChange(e.target.checked)}
                  disabled={isLoading}
                  className={styles.vatCheckbox}
                />
                <span>{t("booking.fullBookingPage.billing.subjectToVat", "Subject to VAT")}</span>
              </label>
              
              {isSubjectToVat && (
                <div className={styles.formField}>
                  <label>{t("booking.fullBookingPage.billing.vatNumber")} *</label>
                  <input 
                    type="text" 
                    value={localBillingInfo.companyVatNumber || billingInfo.companyVatNumber} 
                    onChange={(e) => handleFieldChange('companyVatNumber', e.target.value)} 
                    onBlur={() => setTouchedFields(prev => ({ ...prev, companyVatNumber: true }))}
                    className={`${styles.input} ${hasFieldError('companyVatNumber') ? styles.inputError : ''}`}
                    placeholder={t("booking.fullBookingPage.billing.vatNumberPlaceholder")}
                    disabled={isLoading}
                  />
                </div>
              )}
              
              {!isSubjectToVat && (
                <div className={styles.vatExemptMessage}>
                  <span className={styles.infoIcon}>ℹ️</span>
                  <span className={styles.exemptText}>
                    {t("booking.fullBookingPage.billing.vatExempt", "Your invoice will be issued without VAT as you are not subject to VAT.")}
                  </span>
                </div>
              )}
            </div>
            
            <div className={styles.formField}>
              <label>{t("booking.fullBookingPage.billing.address")} *</label>
              <input 
                type="text" 
                value={localBillingInfo.companyAddress || billingInfo.companyAddress} 
                onChange={(e) => handleFieldChange('companyAddress', e.target.value)} 
                onBlur={() => setTouchedFields(prev => ({ ...prev, companyAddress: true }))}
                className={`${styles.input} ${hasFieldError('companyAddress') ? styles.inputError : ''}`}
                placeholder={t("booking.fullBookingPage.billing.addressPlaceholder")}
                disabled={isLoading}
              />
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <label>{t("booking.fullBookingPage.billing.postalCode")} *</label>
                <input 
                  type="text" 
                  value={localBillingInfo.companyPostalCode || billingInfo.companyPostalCode} 
                  onChange={(e) => handleFieldChange('companyPostalCode', e.target.value)} 
                  onBlur={() => setTouchedFields(prev => ({ ...prev, companyPostalCode: true }))}
                  className={`${styles.input} ${hasFieldError('companyPostalCode') ? styles.inputError : ''}`}
                  placeholder={t("booking.fullBookingPage.billing.postalCodePlaceholder")}
                  disabled={isLoading}
                />
              </div>
              <div className={styles.formField}>
                <label>{t("booking.fullBookingPage.billing.city")} *</label>
                <input 
                  type="text" 
                  value={localBillingInfo.companyCity || billingInfo.companyCity} 
                  onChange={(e) => handleFieldChange('companyCity', e.target.value)} 
                  onBlur={() => setTouchedFields(prev => ({ ...prev, companyCity: true }))}
                  className={`${styles.input} ${hasFieldError('companyCity') ? styles.inputError : ''}`}
                  placeholder={t("booking.fullBookingPage.billing.cityPlaceholder")}
                  disabled={isLoading}
                />
              </div>
            </div>
          </>
        )}
      </div>
      
      {hasAnyBillingErrors() && (
        <MessageBox 
          type="error" 
          message={
            billingType === "individual"
              ? t("booking.fullBookingPage.billing.errors.individualMissingFields")
              : t("booking.fullBookingPage.billing.errors.companyMissingFields")
          } 
        />
      )}
    </div>
  );
};

export default BillingInformation;